<script>
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message.js';
import ContactAPI from 'dashboard/api/contacts.js';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

// Aceleres 2026-07-03: redesign do Encaminhar pra replicar o WhatsApp Web.
// Modal centralizado com overlay desfocado, busca no topo, lista de contatos
// da Base de Clientes com avatar + nome + info secundária + CHECKBOX (seleção
// múltipla, o item permanece na lista marcado), e campo "Adicione uma
// mensagem..." no rodapé.
// No envio: pra cada destino, o texto adicional vai ANTES (como msg de texto
// normal) e depois vem o forward — igual ao WhatsApp Web.
// Props/emits mantidos idênticos (montagem em MessagesView.vue não muda).
const CONTACTS_PER_PAGE = 15;

export default {
  components: { Avatar },
  props: {
    show: { type: Boolean, default: false },
    conversationId: { type: Number, required: true },
    selectedMessages: { type: Array, default: () => [] },
  },
  emits: ['cancel', 'update:show', 'success'],
  data() {
    return {
      searchQuery: '',
      // Resultado bruto da Base de Clientes. Itens selecionados continuam na
      // lista (checkbox marcado), igual à tela "Encaminhar para" do WhatsApp.
      rawResults: [],
      selectedConversations: [],
      resolvingContactIds: [],
      additionalMessage: '',
      isSearching: false,
      isForwarding: false,
      searchTimeout: null,
      searchRequestId: 0,
      searchFailed: false,
      copy: {
        dialog: 'Encaminhar mensagem',
        close: 'Fechar',
        title: 'Encaminhar mensagem para',
        selected: 'selecionado',
        selectedPlural: 'selecionados',
        searchPlaceholder: 'Buscar um nome ou número',
        loading: 'Carregando contatos…',
        loadError: 'Não foi possível carregar os contatos.',
        empty: 'Não há contatos disponíveis para encaminhamento.',
        extraPlaceholder: 'Adicione uma mensagem...',
        send: 'Encaminhar',
        recent: 'Contatos cadastrados',
        results: 'Resultados',
      },
    };
  },
  computed: {
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    selectedCount() {
      return this.selectedMessages.length;
    },
    selectedIds() {
      return new Set(this.selectedConversations.map(contact => contact.id));
    },
    // Contatos selecionados continuam na lista (com checkbox marcado), igual
    // à tela "Encaminhar para" do WhatsApp.
    searchResults() {
      return this.rawResults;
    },
    destinationCount() {
      return this.selectedConversations.length;
    },
    canForward() {
      return (
        this.selectedConversations.length > 0 &&
        !this.isForwarding &&
        this.selectedCount > 0
      );
    },
    listHeading() {
      return this.searchQuery.trim() ? this.copy.results : this.copy.recent;
    },
    destinationLabel() {
      if (!this.destinationCount) return '';
      const suffix =
        this.destinationCount === 1
          ? this.copy.selected
          : this.copy.selectedPlural;
      return `${this.destinationCount} ${suffix}`;
    },
    primaryMessage() {
      return this.selectedMessages[0] || null;
    },
    previewAttachment() {
      const attachments = this.primaryMessage?.attachments;
      return Array.isArray(attachments) ? attachments[0] || null : null;
    },
    previewImageUrl() {
      const attachment = this.previewAttachment || {};
      const fileType = attachment.fileType || attachment.file_type;
      if (fileType !== 'image') return '';
      return (
        attachment.thumbUrl ||
        attachment.thumb_url ||
        attachment.dataUrl ||
        attachment.data_url ||
        ''
      );
    },
    previewLabel() {
      if (this.selectedCount > 1) {
        return `${this.selectedCount} mensagens`;
      }

      const content = String(this.primaryMessage?.content || '').trim();
      if (content) return content;

      const fileType =
        this.previewAttachment?.fileType || this.previewAttachment?.file_type;
      const labels = {
        image: 'Imagem',
        video: 'Vídeo',
        audio: 'Áudio',
        file: 'Documento',
      };
      return labels[fileType] || 'Mensagem';
    },
    previewIcon() {
      if (this.selectedCount > 1) return 'i-lucide-messages-square';

      const fileType =
        this.previewAttachment?.fileType || this.previewAttachment?.file_type;
      const icons = {
        image: 'i-lucide-image',
        video: 'i-lucide-video',
        audio: 'i-lucide-mic-2',
        file: 'i-lucide-file-text',
      };
      return icons[fileType] || 'i-lucide-message-square-text';
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) {
          document.removeEventListener('keydown', this.onKeydown);
          document.addEventListener('keydown', this.onKeydown);
          document.body.style.overflow = 'hidden';
          this.loadContacts('');
          this.$nextTick(() => this.$refs.searchInput?.focus());
        } else {
          document.removeEventListener('keydown', this.onKeydown);
          document.body.style.overflow = '';
          this.reset();
        }
      },
    },
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape' && this.show) this.handleCancel();
    },
    reset() {
      clearTimeout(this.searchTimeout);
      this.searchRequestId += 1;
      this.searchQuery = '';
      this.rawResults = [];
      this.selectedConversations = [];
      this.resolvingContactIds = [];
      this.additionalMessage = '';
      this.isSearching = false;
      this.isForwarding = false;
      this.searchFailed = false;
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchRequestId += 1;
      this.rawResults = [];
      this.isSearching = true;
      this.searchFailed = false;
      const query = this.searchQuery.trim();
      this.searchTimeout = setTimeout(
        () => this.loadContacts(query),
        query ? 250 : 0
      );
    },
    extractContacts(data) {
      const payload = data?.payload;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.contacts)) return payload.contacts;
      return [];
    },
    async fetchContactsPage(query, page) {
      if (query) {
        return ContactAPI.search(query, page, 'name');
      }

      return ContactAPI.get(page, 'name');
    },
    appendUniqueContacts(currentContacts, newContacts) {
      const contactsById = new Map(
        currentContacts.map(contact => [contact.id, contact])
      );

      newContacts.forEach(contact => {
        if (contact?.id != null) contactsById.set(contact.id, contact);
      });

      return Array.from(contactsById.values());
    },
    async loadContacts(query) {
      const requestId = this.searchRequestId + 1;
      this.searchRequestId = requestId;
      this.isSearching = true;
      this.searchFailed = false;

      try {
        const { data } = await this.fetchContactsPage(query, 1);
        if (requestId !== this.searchRequestId) return;
        let contacts = this.extractContacts(data);
        this.rawResults = contacts;

        const meta = data?.meta || {};
        const totalCount = Number(meta.count || 0);
        const totalPages = query
          ? null
          : Math.ceil(totalCount / CONTACTS_PER_PAGE);
        let nextPage = 2;
        let hasMore = query
          ? Boolean(meta.has_more)
          : totalPages > 1 || contacts.length === CONTACTS_PER_PAGE;

        // A primeira página aparece imediatamente. As demais são carregadas
        // em segundo plano para que a abertura permaneça rápida mesmo quando
        // a Base de Clientes tiver muitos registros.
        while (hasMore) {
          if (requestId !== this.searchRequestId) return;

          let nextData;
          try {
            // As páginas seguintes dependem do cursor informado pela API.
            // eslint-disable-next-line no-await-in-loop
            const response = await this.fetchContactsPage(query, nextPage);
            nextData = response.data;
          } catch {
            // Mantém as páginas já exibidas; uma falha posterior não deve
            // transformar uma lista válida em estado de erro.
            break;
          }

          if (requestId !== this.searchRequestId) return;

          const nextContacts = this.extractContacts(nextData);
          contacts = this.appendUniqueContacts(contacts, nextContacts);
          this.rawResults = contacts;

          const nextMeta = nextData?.meta || {};
          hasMore = query
            ? Boolean(nextMeta.has_more)
            : nextPage < totalPages && nextContacts.length > 0;
          nextPage += 1;

          if (nextContacts.length === 0) break;
        }
      } catch {
        if (requestId !== this.searchRequestId) return;
        this.rawResults = [];
        this.searchFailed = true;
      } finally {
        if (requestId === this.searchRequestId) {
          this.isSearching = false;
        }
      }
    },
    contact(record) {
      return record?.contact || record?.meta?.sender || record || {};
    },
    contactName(record) {
      const contact = this.contact(record);
      return (
        contact.name ||
        contact.phone_number ||
        contact.phoneNumber ||
        `Contato #${record.id}`
      );
    },
    contactThumb(record) {
      const contact = this.contact(record);
      return contact.thumbnail || contact.avatar_url || contact.avatarUrl || '';
    },
    // O contato da Base de Clientes não traz a última mensagem. Priorizamos
    // telefone/e-mail e mantemos suporte ao shape antigo de conversas.
    contactSecondary(record) {
      const last =
        record?.message ||
        record?.messages?.[0] ||
        record?.last_non_activity_message;
      if (last && last.content) return String(last.content).slice(0, 80);
      const contact = this.contact(record);
      const phone = contact.phone_number || contact.phoneNumber;
      if (phone) return phone;
      if (contact.email) return contact.email;
      return '';
    },
    isSelected(contact) {
      return this.selectedIds.has(contact.id);
    },
    isResolvingContact(contact) {
      return this.resolvingContactIds.includes(contact.id);
    },
    async toggleContact(contact) {
      if (this.isSelected(contact)) {
        this.selectedConversations = this.selectedConversations.filter(
          selectedContact => selectedContact.id !== contact.id
        );
        return;
      }

      if (this.isResolvingContact(contact)) return;

      this.resolvingContactIds = [...this.resolvingContactIds, contact.id];
      try {
        const { data } = await ContactAPI.getConversations(contact.id);
        const conversations = Array.isArray(data?.payload) ? data.payload : [];
        const destination = conversations.find(
          conversation =>
            String(conversation.id) !== String(this.conversationId)
        );

        if (!destination) {
          useAlert(
            'Este contato não possui outra conversa disponível para encaminhamento.'
          );
          return;
        }

        this.selectedConversations = [
          ...this.selectedConversations,
          { ...contact, targetConversationId: destination.id },
        ];
      } catch {
        useAlert('Não foi possível carregar a conversa deste contato.');
      } finally {
        this.resolvingContactIds = this.resolvingContactIds.filter(
          id => id !== contact.id
        );
      }
    },
    async handleForward() {
      if (!this.canForward) return;
      this.isForwarding = true;
      const messageIds = this.selectedMessages.map(m => m.id);
      const extra = this.additionalMessage.trim();
      // Pra cada destino: (1) mensagem adicional ANTES (texto normal),
      // (2) forward das mensagens originais. allSettled: se um destino falha,
      // os outros continuam; o alerta reporta quantos foram ok.
      const results = await Promise.allSettled(
        this.selectedConversations.map(async dest => {
          const targetConversationId = dest.targetConversationId;

          if (extra) {
            await MessageApi.create({
              conversationId: targetConversationId,
              message: extra,
              private: false,
            });
          }
          return MessageApi.forward(
            this.conversationId,
            targetConversationId,
            messageIds
          );
        })
      );
      const okCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.length - okCount;
      this.isForwarding = false;
      if (failCount === 0) {
        useAlert(this.$t('CONVERSATION.FORWARD.SUCCESS'));
        this.$emit('success');
        this.localShow = false;
      } else if (okCount > 0) {
        useAlert(
          `Encaminhado para ${okCount} de ${results.length} conversas. ${failCount} falharam.`
        );
        this.$emit('success');
        this.localShow = false;
      } else {
        useAlert(this.$t('CONVERSATION.FORWARD.ERROR'));
      }
    },
    handleCancel() {
      if (this.isForwarding) return;
      this.localShow = false;
      this.$emit('cancel');
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <Transition name="acl-fwd-fade">
      <div v-if="show" class="acl-forward-overlay" @click.self="handleCancel">
        <div
          class="acl-forward-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="copy.dialog"
        >
          <!-- Header -->
          <div class="acl-forward-header">
            <button
              type="button"
              class="acl-forward-close"
              :aria-label="copy.close"
              @click="handleCancel"
            >
              <span class="i-lucide-x" />
            </button>
            <div class="acl-forward-header__titles">
              <h2 class="acl-forward-title">{{ copy.title }}</h2>
              <span v-if="destinationCount > 0" class="acl-forward-subtitle">
                {{ destinationLabel }}
              </span>
            </div>
          </div>

          <!-- Busca -->
          <div class="acl-forward-search">
            <span class="i-lucide-search acl-forward-search__icon" />
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="acl-forward-search__input"
              :placeholder="copy.searchPlaceholder"
              autocomplete="off"
              @input="onSearchInput"
            />
            <span
              v-if="isSearching"
              class="i-lucide-loader acl-forward-search__spinner"
            />
          </div>

          <!-- Lista da Base de Clientes -->
          <div class="acl-forward-list">
            <div class="acl-forward-list__heading">
              {{ listHeading }}
            </div>
            <div
              v-if="isSearching && searchResults.length === 0"
              class="acl-forward-empty"
            >
              <span class="i-lucide-loader acl-forward-empty__spinner" />
              {{ copy.loading }}
            </div>
            <div v-else-if="searchFailed" class="acl-forward-empty">
              {{ copy.loadError }}
            </div>
            <div
              v-else-if="searchResults.length === 0"
              class="acl-forward-empty"
            >
              {{ copy.empty }}
            </div>
            <button
              v-for="contactRecord in searchResults"
              :key="contactRecord.id"
              type="button"
              class="acl-forward-item"
              :class="{
                'acl-forward-item--selected': isSelected(contactRecord),
              }"
              :disabled="isResolvingContact(contactRecord) || isForwarding"
              :aria-busy="isResolvingContact(contactRecord)"
              @click="toggleContact(contactRecord)"
            >
              <span
                class="acl-forward-check"
                :class="{ 'acl-forward-check--on': isSelected(contactRecord) }"
                aria-hidden="true"
              >
                <span
                  v-if="isResolvingContact(contactRecord)"
                  class="i-lucide-loader animate-spin"
                />
                <span
                  v-else-if="isSelected(contactRecord)"
                  class="i-lucide-check"
                />
              </span>
              <Avatar
                :name="contactName(contactRecord)"
                :src="contactThumb(contactRecord)"
                :size="48"
                rounded-full
              />
              <div class="acl-forward-item__info">
                <span class="acl-forward-item__name">{{
                  contactName(contactRecord)
                }}</span>
                <span
                  v-if="contactSecondary(contactRecord)"
                  class="acl-forward-item__secondary"
                >
                  {{ contactSecondary(contactRecord) }}
                </span>
              </div>
            </button>
          </div>

          <!-- Footer: prévia da mensagem + comentário opcional + enviar -->
          <div class="acl-forward-footer">
            <div class="acl-forward-preview" :title="previewLabel">
              <img
                v-if="previewImageUrl"
                :src="previewImageUrl"
                :alt="previewLabel"
                class="acl-forward-preview__image"
              />
              <div v-else class="acl-forward-preview__fallback">
                <span :class="previewIcon" />
                <span>{{ previewLabel }}</span>
              </div>
              <span v-if="selectedCount > 1" class="acl-forward-preview__count">
                {{ selectedCount }}
              </span>
            </div>
            <textarea
              v-model="additionalMessage"
              class="acl-forward-extra"
              :placeholder="copy.extraPlaceholder"
              rows="2"
              :disabled="isForwarding"
              @keydown.ctrl.enter.prevent="handleForward"
              @keydown.meta.enter.prevent="handleForward"
            />
            <button
              type="button"
              class="acl-forward-send"
              :class="{ 'acl-forward-send--disabled': !canForward }"
              :disabled="!canForward"
              :aria-label="copy.send"
              @click="handleForward"
            >
              <span
                v-if="isForwarding"
                class="i-lucide-loader acl-forward-send__spinner"
              />
              <span v-else class="i-lucide-send" />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Overlay escuro + blur = fundo perde foco, igual WhatsApp Web. */
.acl-forward-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(11, 20, 17, 0.66);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}
.acl-forward-modal {
  width: 100%;
  max-width: 600px;
  height: min(88dvh, 820px);
  max-height: calc(100dvh - 40px);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 18px 52px rgba(0, 0, 0, 0.34);
  overflow: hidden;
}

/* Header: X à esquerda + título (e contagem quando houver selecionados). */
.acl-forward-header {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 72px;
  padding: 16px 28px;
  border-bottom: 1px solid #e9edef;
  box-sizing: border-box;
}
.acl-forward-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: #54656f;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.12s,
    color 0.12s;
}
.acl-forward-close > span {
  width: 20px;
  height: 20px;
  background-color: currentColor;
}
.acl-forward-close:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #111b21;
}
.acl-forward-header__titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.acl-forward-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: #111b21;
}
.acl-forward-subtitle {
  font-size: 13px;
  color: #667781;
  line-height: 1.3;
  margin-top: 2px;
}

/* Busca: pílula arredondada estilo WhatsApp, ícone de lupa à esquerda. */
.acl-forward-search {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  margin: 16px 28px 12px;
  padding: 0 18px;
  border: 1.5px solid #aebac1;
  border-radius: 26px;
  background: #ffffff;
  box-sizing: border-box;
  transition:
    border-color 0.12s,
    box-shadow 0.12s;
}
.acl-forward-search:focus-within {
  border-color: #00a884;
  box-shadow: 0 0 0 1px #00a884;
}
.acl-forward-search__icon {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  background-color: #54656f;
  pointer-events: none;
}
.acl-forward-search__input {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  height: 48px;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  border-radius: 0;
  background: transparent !important;
  color: #111b21;
  font-size: 15px;
  line-height: 48px;
  outline: none !important;
  box-shadow: none !important;
  box-sizing: border-box;
}
.acl-forward-search__input::placeholder {
  color: #667781;
}
.acl-forward-search__spinner {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  background-color: #667781;
  animation: acl-fwd-spin 0.8s linear infinite;
}

/* Lista rolável de conversas. */
.acl-forward-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 0 14px;
  scrollbar-width: thin;
}
.acl-forward-list__heading {
  padding: 14px 28px 10px;
  color: #667781;
  font-size: 14px;
  font-weight: 500;
}
.acl-forward-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 24px;
  text-align: center;
  font-size: 14px;
  color: #667781;
}
.acl-forward-empty__spinner {
  width: 18px;
  height: 18px;
  background-color: currentColor;
  animation: acl-fwd-spin 0.8s linear infinite;
}
.acl-forward-item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 74px;
  padding: 10px 28px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}
.acl-forward-item:hover {
  background: #f5f6f6;
}
.acl-forward-item--selected {
  background: #f0f7f4;
}
.acl-forward-item--selected:hover {
  background: #e6f2ec;
}
.acl-forward-item__info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.acl-forward-item__name {
  font-size: 16px;
  font-weight: 600;
  color: #111b21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.acl-forward-item__secondary {
  font-size: 14px;
  color: #667781;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Checkbox quadrado à esquerda, como no seletor do WhatsApp. */
.acl-forward-check {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #aebac1;
  border-radius: 4px;
  background: #ffffff;
  transition:
    background 0.12s,
    border-color 0.12s;
}
.acl-forward-check > span {
  width: 14px;
  height: 14px;
  background-color: #ffffff;
}
.acl-forward-check--on {
  background: #00a884;
  border-color: #00a884;
}

/* Rodapé com prévia do conteúdo, comentário opcional e envio. */
.acl-forward-footer {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) 48px;
  align-items: center;
  gap: 14px;
  padding: 16px 20px 18px;
  border-top: 1px solid #e9edef;
  background: #f0f2f5;
}
.acl-forward-preview {
  position: relative;
  width: 88px;
  height: 88px;
  overflow: hidden;
  border-radius: 7px;
  background: #dfe5e7;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.16);
}
.acl-forward-preview__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.acl-forward-preview__fallback {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px;
  color: #54656f;
  font-size: 10px;
  line-height: 1.15;
  text-align: center;
  box-sizing: border-box;
}
.acl-forward-preview__fallback > span:first-child {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  background-color: currentColor;
}
.acl-forward-preview__fallback > span:last-child {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.acl-forward-preview__count {
  position: absolute;
  right: 4px;
  bottom: 4px;
  display: inline-flex;
  min-width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border-radius: 11px;
  color: #ffffff;
  background: rgba(17, 27, 33, 0.78);
  font-size: 11px;
  font-weight: 700;
  box-sizing: border-box;
}
.acl-forward-extra {
  width: 100%;
  min-height: 88px;
  max-height: 132px;
  padding: 14px 16px;
  resize: none;
  border: none;
  border-radius: 7px;
  background: #ffffff;
  color: #111b21;
  font-size: 15px;
  line-height: 1.45;
  outline: none;
  box-sizing: border-box;
  transition: box-shadow 0.12s;
}
.acl-forward-extra::placeholder {
  color: #8696a0;
}
.acl-forward-extra:focus {
  box-shadow: inset 0 0 0 1px rgba(0, 168, 132, 0.6);
}
.acl-forward-send {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: #00a884;
  color: #ffffff;
  cursor: pointer;
  transition:
    background 0.12s,
    opacity 0.12s;
}
.acl-forward-send > span {
  width: 20px;
  height: 20px;
  background-color: currentColor;
}
.acl-forward-send:hover {
  background: #008f72;
}
.acl-forward-send--disabled,
.acl-forward-send:disabled {
  background: #d1d7db;
  cursor: not-allowed;
  opacity: 0.85;
}
.acl-forward-send__spinner {
  animation: acl-fwd-spin 0.8s linear infinite;
}

@media (max-width: 767px) {
  .acl-forward-overlay {
    align-items: flex-end;
    padding: 0;
    background: rgba(11, 20, 17, 0.58);
  }

  .acl-forward-modal {
    width: 100%;
    max-width: none;
    height: min(92dvh, 780px);
    max-height: calc(100dvh - 12px);
    border-radius: 20px 20px 0 0;
  }

  .acl-forward-modal::before {
    content: '';
    display: block;
    width: 38px;
    height: 4px;
    margin: 8px auto 0;
    flex: 0 0 4px;
    border-radius: 999px;
    background: #c7cdd1;
  }

  .acl-forward-header {
    min-height: 54px;
    padding: 8px 12px 10px;
  }

  .acl-forward-title {
    font-size: 16px;
  }

  .acl-forward-search {
    margin: 10px 14px 6px;
    min-height: 46px;
    padding: 0 16px;
  }

  .acl-forward-search__input {
    height: 46px;
    font-size: 16px;
    line-height: 46px;
  }

  .acl-forward-list__heading {
    padding: 12px 16px 6px;
  }

  .acl-forward-item {
    min-height: 66px;
    padding: 8px 16px;
  }

  .acl-forward-footer {
    grid-template-columns: 60px minmax(0, 1fr) 44px;
    gap: 10px;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
  }

  .acl-forward-preview {
    width: 60px;
    height: 60px;
  }

  .acl-forward-extra {
    min-height: 60px;
    max-height: 96px;
    font-size: 16px;
  }
}

/* Transições */
.acl-fwd-fade-enter-active,
.acl-fwd-fade-leave-active {
  transition: opacity 0.16s ease;
}
.acl-fwd-fade-enter-from,
.acl-fwd-fade-leave-to {
  opacity: 0;
}
.acl-fwd-fade-enter-active .acl-forward-modal,
.acl-fwd-fade-leave-active .acl-forward-modal {
  transition:
    transform 0.18s ease,
    opacity 0.18s ease;
}
.acl-fwd-fade-enter-from .acl-forward-modal,
.acl-fwd-fade-leave-to .acl-forward-modal {
  transform: scale(0.96) translateY(8px);
  opacity: 0;
}
@keyframes acl-fwd-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Dark mode */
:global(.dark) .acl-forward-modal {
  background: #202c33;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.55);
}
:global(.dark) .acl-forward-header {
  border-bottom-color: #2a3942;
}
:global(.dark) .acl-forward-close {
  color: #aebac1;
}
:global(.dark) .acl-forward-close:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #e9edef;
}
:global(.dark) .acl-forward-title {
  color: #e9edef;
}
:global(.dark) .acl-forward-subtitle {
  color: #8696a0;
}
:global(.dark) .acl-forward-search__icon {
  background-color: #8696a0;
}
:global(.dark) .acl-forward-search__input {
  background: transparent !important;
  border: none !important;
  color: #e9edef;
}
:global(.dark) .acl-forward-search__input::placeholder {
  color: #8696a0;
}
:global(.dark) .acl-forward-search {
  background: #2a3942;
  border-color: #8696a0;
}
:global(.dark) .acl-forward-search:focus-within {
  border-color: #00a884;
}
:global(.dark) .acl-forward-search__spinner {
  background-color: #8696a0;
}
:global(.dark) .acl-forward-empty {
  color: #8696a0;
}
:global(.dark) .acl-forward-list__heading {
  color: #8696a0;
}
:global(.dark) .acl-forward-item:hover {
  background: #2a3942;
}
:global(.dark) .acl-forward-item--selected,
:global(.dark) .acl-forward-item--selected:hover {
  background: #233633;
}
:global(.dark) .acl-forward-item__name {
  color: #e9edef;
}
:global(.dark) .acl-forward-item__secondary {
  color: #8696a0;
}
:global(.dark) .acl-forward-check {
  border-color: #3b4a54;
  background: #202c33;
}
:global(.dark) .acl-forward-check--on {
  border-color: #00a884;
  background: #00a884;
}
:global(.dark) .acl-forward-footer {
  border-top-color: #2a3942;
  background: #111b21;
}
:global(.dark) .acl-forward-extra {
  background: #2a3942;
  border-color: #2a3942;
  color: #e9edef;
}
:global(.dark) .acl-forward-extra::placeholder {
  color: #8696a0;
}
:global(.dark) .acl-forward-preview {
  background: #2a3942;
}
:global(.dark) .acl-forward-preview__fallback {
  color: #aebac1;
}
</style>
